import React, { Children, Component } from "react"
import { getBellringerQuestionForReview, getBellringersImageURL, getBellringerTestMetaData, upsertUserBellringer } from "../../services/bellringers"
import { MathKatex2 } from "../../components/MathKatex"
import { CDN_PUB_URL_BELLRINGERS } from "../../constants"
import { SimpleTimer } from "../../services/utils/simple-timer"
import SplitPanel from "../../components/SplitPanel"
import Highlighter from "../../components/Highlighter"
import { Bookmark } from "../../components/Bookmark"
import AnswersGroup from "./AnswersGroup"

interface Props {
    question: any
}

interface State {
    allowCrossOut: boolean
}

export default class BellringersQuestionView extends React.Component<Props, State> {
    simpleTimer = new SimpleTimer()

    constructor(props) {
        super(props)
        this.state = {
            allowCrossOut: false
        }
    }

    prepareOption = (option: string) => {
        if (!option) return ""
        if (option.includes("$CDN")) {
            let url = option.replace("$CDN", CDN_PUB_URL_BELLRINGERS)
            return `<img src=${url} class="question-option" alt="option"/>`
        }
        return option
    }

    handleCrossOut = (option, crossed) => {
        let {question} = this.props
        if (!question.crossedOutInfo) {
            question.crossedOutInfo = {}
        }
        question.crossedOutInfo[option] = crossed
        this.setState({})
    }

    handleAnswer = (answer) => {
        let {question} = this.props
        question.userSubmittedAnswer = answer
        this.submitUserAction(true)
        this.setState({})
    }

    handleBookmark = () => {
        let {question} = this.props
        question.bookmarked = !question.bookmarked
        this.submitUserAction()
        this.setState({})
    }

    submitUserAction = async (processStudyTime=false) => {
        try {
            let {question} = this.props
            let {id, userSubmittedAnswer, bookmarked} = question
            let questionAnswerTime = processStudyTime && this.simpleTimer.lapsedDiffInSeconds() || 0

            await upsertUserBellringer(id, userSubmittedAnswer, bookmarked, questionAnswerTime)
        }
        catch (err) {
            console.error(err)
        }
    }

    handleToggleCrossOut = () => {
        let {allowCrossOut} = this.state
        this.setState({allowCrossOut: !allowCrossOut})
    }

    render() {
        let {question} = this.props

        if (!question) return null

        let {id, optionA, optionB, optionC, optionD} = question

        if (!id) return null 

        this.simpleTimer.resetLapse()

        let {allowCrossOut} = this.state
        let meta = getBellringerTestMetaData(id)
        let {subject, grade, week} = meta
        let options = [
            { value: "A", label: this.prepareOption(optionA) },
            { value: "B", label: this.prepareOption(optionB) },
            { value: "C", label: this.prepareOption(optionC) },
            { value: "D", label: this.prepareOption(optionD) },
        ]

        return (
            subject === "math" ?
                <BellringersMathView
                    question={question}
                    options={options}
                    allowCrossOut={allowCrossOut}
                    onAnswer={this.handleAnswer}
                    onCrossOut={this.handleCrossOut}
                    onBookmark={this.handleBookmark}
                    onToggleCrossOut={this.handleToggleCrossOut}
                />
                :
                <BellringersNonMathView
                    question={question}
                    options={options}
                    allowCrossOut={allowCrossOut}
                    onAnswer={this.handleAnswer}
                    onCrossOut={this.handleCrossOut}
                    onBookmark={this.handleBookmark}
                    onToggleCrossOut={this.handleToggleCrossOut}
                />
        )
    }
}

interface IBellringersQuestionProps {
    question: any
    options: any[]
    allowCrossOut: boolean
    onAnswer: (question) => void
    onCrossOut: (option, crossed) => void
    onBookmark: () => void
    onToggleCrossOut: () => void
}

// Math: Single-panel view
function BellringersMathView(props: IBellringersQuestionProps) {
    let {question, options, allowCrossOut, onAnswer, onCrossOut, onBookmark, onToggleCrossOut} = props
    let {id, questionNo, questionText, questionImage, questionQualifier, userSubmittedAnswer, crossedOutInfo} = question
    let bellringersImageURL = getBellringersImageURL(questionImage)
    let alt = `Image of question ${questionNo}`

    return (
        <div className="single-panel-container">
            <QuestionInfo question={question} onBookmark={onBookmark} onAllowCrossOut={onToggleCrossOut}/>
            <MathKatex2 mixedContent={questionText}/>
            {questionImage && <img src={bellringersImageURL} alt={alt} />}
            <MathKatex2 mixedContent={questionQualifier}/>
            <AnswersGroup
                questionNumber={questionNo}
                options={options}
                selectedAnswer={userSubmittedAnswer || ""}
                allowCrossOut={allowCrossOut}
                crossedOutInfo={crossedOutInfo}
                onSelect={onAnswer}
                onCrossOut={onCrossOut}
            />
        </div>
    )
}


// Other subjects: Two-panel view
function BellringersNonMathView(props: IBellringersQuestionProps) {
    let {question, options, allowCrossOut, onAnswer, onCrossOut, onBookmark, onToggleCrossOut} = props
    let {id, questionNo, questionText, questionImage, questionQualifier, userSubmittedAnswer, crossedOutInfo} = question
    let bellringersImageURL = getBellringersImageURL(questionImage)
    let alt = `Image of question ${questionNo}`
    let highlighterEnabled = true
    let infoRight = questionQualifier
    let infoLeft = questionImage ?
        `${questionText} <br/> <img src="${bellringersImageURL}" alt="${alt}" />`
        :
        questionText

    function handleHighlightLeft(info) {
        console.log("OT: handleHighlightLeft", info)
    }

    function handleHighlightRight(info) {
        console.log("OT: handleHighlightRight", info)
    }

    return (
        <div className="double-panel-container">
        <SplitPanel classNameLeftPanel="split-panel-left" classNameRightPanel="split-panel-right"
            panelLeft={
                <div className="split-panel--inner">
                    <Highlighter enabled={highlighterEnabled} onHighlight={handleHighlightLeft}>
                        <MathKatex2 mixedContent={infoLeft} />
                    </Highlighter>
                </div>
            }
            panelRight={
                <div className="split-panel--inner">
                    <QuestionInfo question={question} onBookmark={onBookmark} onAllowCrossOut={onToggleCrossOut}/>
                    <Highlighter enabled={highlighterEnabled} onHighlight={handleHighlightRight}>
                        <MathKatex2 mixedContent={infoRight} />
                    </Highlighter>
                    <AnswersGroup
                        questionNumber={questionNo}
                        options={options}
                        selectedAnswer={userSubmittedAnswer}
                        allowCrossOut={allowCrossOut}
                        crossedOutInfo={crossedOutInfo}
                        onSelect={onAnswer}
                        onCrossOut={onCrossOut}
                    />
                </div>
            }
            allowResize={true}
        />
        </div>
    )
}

function QuestionInfo(props) {
    let {question, onBookmark, onAllowCrossOut} = props
    let {questionNo: questionNumber, bookmarked} = question
    let bookmarkLabel = "Mark for Review"

    return (
        <div className="question-info-bar mb-3">
            <div className="question-number-area">{questionNumber}</div>
            <div className="bookmark-area"><Bookmark label={bookmarkLabel} bookmarked={bookmarked} onBookmark={onBookmark}/></div>
            <div className="cross-out-area">
                <button aria-label="Cross out answer choices you think are wrong." onClick={onAllowCrossOut}>
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M29.62 0H2.88C1.29 0 0 1.29 0 2.88V29.62C0 31.21 1.29 32.5 2.88 32.5H29.62C31.21 32.5 32.5 31.21 32.5 29.62V2.88C32.5 1.29 31.21 0 29.62 0ZM2.88 1.81H29.43L19.43 11.81C18.91 11.41 18.19 11.21 17.26 11.2H12.39V18.85L12.24 19L9.48 11.2H7.11L3.53 21.23H5.72L6.41 19.17H10.11L10.58 20.66L1.81 29.43V2.88C1.81 2.29 2.29 1.81 2.88 1.81ZM14.44 16.81L14.4 16.85V16.81H14.44ZM14.4 15.15V12.94H16.61C17.1 12.94 17.49 12.99 17.81 13.1C17.89 13.13 17.97 13.18 18.03 13.23L16.11 15.15H14.41H14.4ZM9.54 17.43H6.99L8.28 13.48L9.53 17.43H9.54ZM29.62 30.69H3.11L12.58 21.22L14.39 19.41L16.98 16.81L20.31 13.48L21.04 12.75C21.04 12.75 21.02 12.77 21.01 12.79L22.11 11.69C22.11 11.69 22.08 11.72 22.06 11.73L30.69 3.11V29.63C30.69 30.22 30.21 30.7 29.62 30.7V30.69ZM22.85 13.71C22.41 14.33 22.19 15.19 22.19 16.32C22.19 17.45 22.42 18.29 22.89 18.85C23.36 19.41 23.95 19.69 24.66 19.69C25.37 19.69 25.96 19.45 26.34 18.97C26.55 18.71 26.73 18.32 26.87 17.81H28.95C28.77 18.9 28.31 19.79 27.56 20.48C26.81 21.17 25.85 21.51 24.69 21.51C23.25 21.51 22.11 21.05 21.29 20.12C20.99 19.79 20.77 19.39 20.58 18.97C20.51 19.26 20.4 19.54 20.23 19.8C20.04 20.11 19.8 20.38 19.52 20.59C19.2 20.84 18.82 21 18.38 21.09C17.94 21.18 17.47 21.23 16.95 21.23H15.15L16.89 19.49C17.32 19.49 17.65 19.43 17.89 19.32C18.33 19.1 18.56 18.68 18.56 18.07C18.56 17.99 18.55 17.92 18.54 17.85L19.97 16.42C19.97 16.42 20.03 16.47 20.06 16.5C20.06 16.45 20.06 16.4 20.05 16.34L25.31 11.08C26.62 11.23 27.62 11.74 28.28 12.68C28.71 13.3 28.94 13.93 28.97 14.55H26.88C26.74 14.07 26.57 13.71 26.35 13.46C25.97 13.02 25.4 12.81 24.66 12.81C23.92 12.81 23.3 13.12 22.86 13.73L22.85 13.71Z" fill="black"/>
                </svg>
                <span className="sr-only">ABC</span>
                </button>
            </div>
        </div>
    )
}


